import React, { useMemo } from 'react'
import { Fetch } from 'croods'
import { css } from '@emotion/core'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import MetaTags from 'react-meta-tags'
import { useLocation } from '@reach/router'
import get from 'lodash/get'
import map from 'lodash/map'
import kebabCase from 'lodash/kebabCase'

import GuideSkeleton from 'guides/show/skeletons'
import useScrollToElement from 'utils/useScrollToElement'
import PageTitle from 'core/PageTitle'

import RichText from 'ui/RichText'
import useLogEventOnRender from 'amplitude/useLogEventOnRender'
import NotFound from 'pages/NotFound'
import getFormatedContent from './getFormatedContent'
import Header from './Header'
import FullHeader from './FullHeader'
import Drawer from './drawer'
import literaryDevicesCategories from './literaryDevices/categories'

const buildBreadCrumbs = page => {
  const resourceType = get(page, 'customFieldValues.resourceType')
  const category = get(page, 'customFieldValues.category')

  switch (resourceType) {
    case 'literary-elements-devices': {
      const root = [
        { link: '/', label: 'Home' },
        { link: '/literary-devices', label: 'Literary Devices' },
      ]
      const categoryData = literaryDevicesCategories.find(
        ({ value }) => value === category,
      )

      if (categoryData) {
        return [
          ...root,
          { link: `/literary-devices#${category}`, label: categoryData.label },
        ]
      }

      return root
    }
    case 'book-club-resources': {
      return [
        { link: '/', label: 'Home' },
        { link: '/resources', label: 'Other Resources' },
        {
          link: '/resources#book-club-resources',
          label: 'Book Club Resources',
        },
      ]
    }
    case 'deep-dives': {
      return [
        { link: '/', label: 'Home' },
        { link: '/resources', label: 'Other Resources' },
        {
          link: '/resources#super-summary-deep-dives',
          label: 'SuperSummary Deep Dives',
        },
      ]
    }
    case 'author-genre-resources': {
      return [
        { link: '/', label: 'Home' },
        { link: '/resources', label: 'Other Resources' },
        {
          link: '/resources#author-and-genre-resources',
          label: 'Author and Genre Resources',
        },
      ]
    }
    case 'homework-learning-resources': {
      return [
        { link: '/', label: 'Home' },
        { link: '/resources', label: 'Other Resources' },
        {
          link: '/resources#homework-and-learning-resources',
          label: 'Homework and Learning Resources',
        },
      ]
    }
    default: {
      return null
    }
  }
}

const Content = ({ page, slug }) => {
  const classes = useStyles()
  const { href, hash } = useLocation()
  useScrollToElement(hash)

  useLogEventOnRender('View Secondary Page - Resources', {
    'Page Slug': slug,
    URL: href,
  })

  const title = get(page, 'customFieldValues.title')
  const description = get(page, 'customFieldValues.description')
  const content = get(page, 'customFieldValues.content')
  const header = get(page, 'customFieldValues.header')
  const subheader = get(page, 'customFieldValues.subheader')
  const breadcrumbs = useMemo(() => buildBreadCrumbs(page), [page])

  const contentFormated = getFormatedContent(content)
  const contentLength = contentFormated.length

  return (
    <>
      <PageTitle>{title}</PageTitle>
      <MetaTags>
        <meta property="og:type" content="article" />
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={href} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:description" content={description} />
      </MetaTags>
      {breadcrumbs && (
        <FullHeader
          header={header?.toLowerCase()}
          subheader={subheader}
          breadcrumbs={breadcrumbs}
        />
      )}
      <Grid item xs container>
        {!breadcrumbs && <Header header={header} subheader={subheader} />}

        <Container maxWidth="md" className={classes.container}>
          <Grid container>
            {contentLength > 1 && (
              <Grid item sm={4} xs={12}>
                <Drawer
                  content={contentFormated}
                  pageSlug={page.slug}
                  hash={hash}
                />
              </Grid>
            )}
            <Grid
              item
              sm={contentLength > 1 ? 8 : 12}
              xs={12}
              className={classes.content}
              css={css`
                a {
                  color: #1f7f89 !important;
                  text-decoration: none;
                }

                a:hover {
                  text-decoration: underline;
                }

                .ql-editor p {
                  margin-bottom: 1em;
                }

                .ql-editor h1,
                .ql-editor h2 {
                  margin-bottom: 0.5em;
                }

                .ql-editor h3 {
                  margin: 0 0 0.25em;
                }

                h6.richText:nth-of-type(1) h1,
                h6.richText:nth-of-type(1) h2 {
                  margin-top: 8px;
                }

                img {
                  max-width: 100%;
                }
              `}
            >
              {map(contentFormated, (item, index) => (
                <div
                  id={kebabCase(item.title)}
                  key={`${kebabCase(item.title)}-${index}`}
                >
                  <RichText variant="subtitle">{item.content}</RichText>
                </div>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    background: theme.palette.background.paper,
    minHeight: '100%',
    padding: 0,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5, 3),
    },
  },
  content: {
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2),
    },
  },
}))

export default ({ slug }) => {
  const { pathname } = useLocation()
  const checkNestedPagesRegex = /\/.+(\/.+)/

  if (checkNestedPagesRegex.test(pathname)) {
    return <NotFound />
  }

  return (
    <Fetch
      stateId={slug}
      id={slug}
      name="staticPage"
      path="/pages"
      renderLoading={() => <GuideSkeleton />}
      renderError={NotFound}
      render={page => page && <Content page={page} slug={slug} />}
    />
  )
}
